import { primaryButtonClass } from '@/components/common/formStyles'
import { formatNumberDe } from '@/lib/format'
import type { ImportPreviewResponse, TradeDetail } from '@/types/import'
import { TradeSection } from './TradeSection'

interface PreviewCardProps {
  preview: ImportPreviewResponse
  onConfirm: () => void
  onCancel: () => void
  isCommitting: boolean
}

function tradesByStatus(trades: TradeDetail[]) {
  const newOnes: TradeDetail[] = []
  const dups: TradeDetail[] = []
  const skipped: TradeDetail[] = []
  for (const t of trades) {
    if (t.status === 'new') newOnes.push(t)
    else if (t.status === 'duplicate') dups.push(t)
    else if (t.status === 'skipped_sell') skipped.push(t)
  }
  return { newOnes, dups, skipped }
}

export function PreviewCard({ preview, onConfirm, onCancel, isCommitting }: PreviewCardProps) {
  const { summary, warnings, cash, trades } = preview
  const { newOnes, dups, skipped } = tradesByStatus(trades)
  const noNewTrades = summary.trades_new === 0
  const cancelClass =
    'tracked-caps border border-hairline px-6 py-3 text-[11px] text-ink transition-colors hover:bg-panel disabled:cursor-not-allowed disabled:opacity-50'

  return (
    <section className="hairline mt-6 flex flex-col gap-4 border bg-panel p-6">
      <header>
        <h2 className="tracked-caps text-xs text-muted">Preview</h2>
        <p className="mt-1 text-sm text-ink">
          {summary.trades_new} neu · {summary.trades_skipped_duplicates} dup ·{' '}
          {summary.trades_skipped_sells} skip
          {summary.trades_skipped_oversell > 0 && ` · ${summary.trades_skipped_oversell} oversell`}
          {' · '}
          {summary.positions_new} neue Positionen
        </p>
      </header>

      {warnings.length > 0 && (
        <div className="hairline border border-bernstein/40 bg-bernstein/10 p-3">
          <p className="tracked-caps mb-2 text-xs text-bernstein">Warnings ({warnings.length})</p>
          <ul className="list-inside list-disc space-y-1 text-xs text-ink">
            {warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      {cash.length > 0 && (
        <div className="hairline border border-hairline p-3 text-xs">
          <p className="tracked-caps mb-2 text-xs text-muted">Cash-Snapshot</p>
          <ul className="space-y-1 text-ink">
            {cash.map((c, i) => (
              <li key={i}>
                {c.currency} · {c.period_from} bis {c.period_to} · Cash{' '}
                {formatNumberDe(c.starting_cash, 2)} · Deposits +{formatNumberDe(c.deposits, 2)} ·
                Withdrawals −{formatNumberDe(c.withdrawals, 2)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {noNewTrades && (
        <div className="hairline border border-gain/40 bg-gain/10 p-3 text-sm text-gain">
          Keine neuen Trades — alles bereits importiert.
        </div>
      )}

      <div className="flex flex-col gap-3">
        <TradeSection title="Neu" trades={newOnes} defaultOpen />
        <TradeSection title="Duplikate" trades={dups} defaultOpen={false} />
        <TradeSection title="Übersprungen / SELL" trades={skipped} defaultOpen={false} />
      </div>

      <footer className="flex flex-col gap-2">
        {isCommitting && (
          <p className="text-xs text-muted">
            yfinance lädt Metadaten, das kann einige Sekunden dauern …
          </p>
        )}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onConfirm}
            disabled={noNewTrades || isCommitting}
            className={primaryButtonClass}
          >
            {isCommitting ? 'Importiere …' : 'Import bestätigen'}
          </button>
          <button type="button" onClick={onCancel} disabled={isCommitting} className={cancelClass}>
            Abbrechen
          </button>
        </div>
      </footer>
    </section>
  )
}
